import React, { useEffect, useState } from "react";
import { X, ClipboardCheck, Send, Save } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Sl } from "./index";
import { notify } from "../notify";
import { useFormDefinition, fieldsOf, fieldOf, ratingsOf, labelOf, missingRequired } from "../formCatalog";
import { fmtDMY } from "../dateFormat";
import { usePermissions } from "../usePermissions";

// Probation Evaluation Form. HR assigns one, the employee's manager fills
// and submits it, and HR can add HR Comments afterwards. Setting Review
// Status to "Extended" makes the server open a second form, tagged
// "Extended" in this list.
//
// Labels, options, order, required flags and visibility come from
// /api/form-templates, so HR's edits reach this form without a deploy.
const STATUS_COLOR = { Pending: K.a, Completed: K.g };


const fmtDate = (d) => d ? fmtDMY(d) : '—';

export const EmployeeProbationTab = ({ employeeId, role, myEmployeeId }) => {
  const { can: pcan } = usePermissions(true);
  const isHr = pcan('employees', 'probation_eval', null, 'edit');
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const load = () => api.get('/api/probation-evaluations?employee_id=' + employeeId).then((d) => setRows(Array.isArray(d?.rows) ? d.rows : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [employeeId]); // eslint-disable-line

  const assign = async () => {
    setAssigning(true);
    try {
      const r = await api.post('/api/probation-evaluations', { employee_id: employeeId });
      notify('Probation evaluation assigned to the manager', { title: 'Assigned', variant: 'success' });
      load();
      setOpenId(r.id);
    } catch (e) { notify(e.message || 'Failed to assign', { title: 'Error', variant: 'error' }); }
    finally { setAssigning(false); }
  };

  const canSeeRow = (r) => isHr || r.manager_id === myEmployeeId || (r.employee_id === myEmployeeId && r.status === 'Completed');
  const visibleRows = (rows || []).filter(canSeeRow);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] font-bold text-t0 flex items-center gap-2"><ClipboardCheck size={13} color={K.p} /> Probation Evaluation</div>
        {isHr && <Bt s="sm" onClick={assign} disabled={assigning}>{assigning ? 'Assigning…' : 'Assign Probation Evaluation'}</Bt>}
      </div>

      {rows === null ? (
        <div className="text-center p-6 text-t3 text-[14px]">Loading…</div>
      ) : visibleRows.length === 0 ? (
        <div className="text-center p-8 text-t3 text-[14px] bg-bg2 rounded-lg border border-b0">No probation evaluation form yet.</div>
      ) : (
        <div className="grid gap-2">
          {visibleRows.map((r) => (
            <div key={r.id} onClick={() => setOpenId(r.id)} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-bg2 border border-b0 cursor-pointer hover:border-p flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-bold text-t0 truncate flex items-center gap-2">
                  <span className="truncate">{r.evaluation_type === 'Extended' ? 'Probation Extended Review' : 'Probation Review'} — {r.manager_name || r.manager_id || 'Unassigned manager'}</span>
                  {r.evaluation_type === 'Extended' && <Bg c={K.a} sm>Extended</Bg>}
                </div>
                <div className="text-[12px] text-t3 mt-[2px]">Created {fmtDate(r.created_at)}{r.submitted_at ? ` · Submitted ${fmtDate(r.submitted_at)}` : ''}</div>
              </div>
              <Bg c={STATUS_COLOR[r.status] || K.t3} sm dot>{r.status}</Bg>
            </div>
          ))}
        </div>
      )}

      {openId && (
        <ProbationEvalModal
          id={openId}
          role={role}
          myEmployeeId={myEmployeeId}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
};

const RO = ({ label, value }) => (
  <div className="bg-bg2 border border-b0 rounded p-2">
    <div className="text-[12px] text-t3 uppercase">{label}</div>
    <div className="text-[14px] text-t0 mt-[2px]">{value || '—'}</div>
  </div>
);

const ProbationEvalModal = ({ id, role, myEmployeeId, onClose, onChanged }) => {
  const { can: pcan } = usePermissions(true);
  const isHr = pcan('employees', 'probation_eval', null, 'edit');
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/api/probation-evaluations/' + id).then((d) => { setData(d); setVals(d); }).catch(() => setData(null));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  // The layout HR configured; Extended is its own template.
  const { def, loading: defLoading, error: defError } =
    useFormDefinition(data?.evaluation_type === 'Extended' ? 'probation_extended' : 'probation_evaluation');

  if (!data) return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}><div className="bg-bg1 border border-b1 rounded-2xl p-6 text-t3 text-[14px]" onMouseDown={(e) => e.stopPropagation()}>Loading…</div></div>;

  const isManager = data.manager_id === myEmployeeId;
  const canFill = isHr || isManager;
  const isCompleted = data.status === 'Completed';
  const set = (patch) => setVals((prev) => ({ ...prev, ...patch }));
  const sectionTitle = (id, fallback) => (def?.sections || []).find((x) => x.id === id)?.title || fallback;
  const remarksLabel = labelOf(def, 'review', 'remarks');

  const save = async (submit) => {
    if (submit) {
      const missing = ['review', 'decision']
        .map((sec) => missingRequired(def, sec, vals))
        .find(Boolean);
      if (missing) { notify(`"${missing.label}" is required`, { title: 'Error', variant: 'error' }); return; }
    }
    setBusy(true);
    try {
      const body = { ...vals, submit: submit || undefined };
      const r = await api.put('/api/probation-evaluations/' + id, body);
      notify(
        r.extension ? 'Probation extended — an extended evaluation form has been opened'
          : submit ? 'Probation evaluation submitted' : 'Saved',
        { title: r.extension ? 'Extended' : submit ? 'Submitted' : 'Saved', variant: 'success' }
      );
      setData(r); setVals(r);
      onChanged?.();
      if (submit) onClose();
    } catch (e) { notify(e.message || 'Failed to save', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="bg-bg1 border border-b1 rounded-2xl w-full max-w-[640px] max-h-[92vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
          <div className="min-w-0">
            <div className="text-[16px] font-extrabold text-t0 truncate">{data.evaluation_type === 'Extended' ? 'Probation Extended Evaluation Form' : 'Probation Evaluation Form'}</div>
            <div className="text-[14px] text-t3">Initiated by {data.created_by_name || data.created_by || '—'}</div>
          </div>
          <div className="flex items-center gap-2">
            <Bg c={STATUS_COLOR[data.status] || K.t3} sm dot>{data.status}</Bg>
            <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
          </div>
        </div>
        <div className="p-5">
          {defError && <div className="mb-3 p-2 rounded-lg text-[14px]" style={{ background: K.rb, color: K.r }}>{defError}</div>}
          {defLoading && !def ? <div className="text-center py-6 text-t3 text-[14px]">Loading the form…</div> : <>
          <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-2">{sectionTitle('employee', 'Employee Details')}</div>
          <div className="grid grid-cols-2 gap-[8px] mb-4">
            {fieldsOf(def, 'employee').map((f) => (
              <RO key={f.id} label={f.label} value={f.id === 'join_date' ? fmtDate(data.join_date)
                : f.id === 'employee_name' ? (data.employee_name || data.employee_id)
                : f.id === 'manager_name' ? (data.manager_name || data.manager_id)
                : data[f.id]} />
            ))}
          </div>

          <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-2">{sectionTitle('review', 'Performance Review')}</div>
          <div className="grid grid-cols-2 gap-[10px] mb-4">
            {ratingsOf(def, 'review').map((f) => (
              <div key={f.id}>
                <div className="text-[12px] text-t3 uppercase mb-1">{f.label}{f.required && <span style={{ color: K.r }}> *</span>}</div>
                {canFill ? (
                  <Sl options={[{ v: '', l: 'Select' }, ...(f.options || []).map((o) => ({ v: o, l: o }))]} value={vals[f.id] || ''} onChange={(v) => set({ [f.id]: v || null })} disabled={busy} />
                ) : <Bg c={K.t3} sm>{data[f.id] || '—'}</Bg>}
              </div>
            ))}
          </div>

          {remarksLabel && (
            <div className="mb-4">
              <div className="text-[12px] text-t3 uppercase mb-1">{remarksLabel}</div>
              {canFill ? (
                <textarea value={vals.remarks || ''} onChange={(e) => set({ remarks: e.target.value })} disabled={busy}
                  className="w-full min-h-[60px] px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none resize-y" />
              ) : <div className="p-2 rounded-lg bg-bg2 border border-b0 text-[14px] text-t0 whitespace-pre-wrap">{data.remarks || '—'}</div>}
            </div>
          )}

          <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-2">{sectionTitle('decision', 'Review Decision')}</div>
          <div className="grid grid-cols-1 gap-[10px] mb-4">
            {fieldsOf(def, 'decision').map((f) => {
              // HR-only stays HR-only however the template is edited.
              const mayEdit = f.restricted === 'HR only' ? isHr : canFill;
              if (f.id === 'review_status') {
                return (
                  <div key={f.id}>
                    <div className="text-[12px] text-t3 uppercase mb-1">{f.label}{f.required && <span style={{ color: K.r }}> *</span>}</div>
                    {canFill ? (
                      <>
                        <Sl options={(f.options || []).map((o) => ({ v: o, l: o }))} value={vals.review_status || (f.options || [])[0] || 'Pending'} onChange={(v) => set({ review_status: v })} disabled={busy} />
                        {vals.review_status === 'Extended' && data.review_status !== 'Extended' && data.evaluation_type !== 'Extended' && (
                          <div className="text-[12px] text-t3 mt-1">Saving this opens a Probation Extended Evaluation form for the same manager.</div>
                        )}
                      </>
                    ) : <Bg c={K.t3} sm>{data.review_status || 'Pending'}</Bg>}
                  </div>
                );
              }
              return (
                <div key={f.id}>
                  <div className="text-[12px] text-t3 uppercase mb-1">
                    {f.label}{f.required && <span style={{ color: K.r }}> *</span>}
                    {f.restricted && <span className="normal-case text-t3"> ({f.restricted})</span>}
                  </div>
                  {mayEdit ? (
                    <textarea value={vals[f.id] || ''} onChange={(e) => set({ [f.id]: e.target.value })} disabled={busy}
                      className="w-full min-h-[50px] px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none resize-y" />
                  ) : <div className="p-2 rounded-lg bg-bg2 border border-b0 text-[14px] text-t0 whitespace-pre-wrap">{data[f.id] || '—'}</div>}
                  {f.note && <div className="text-[12px] text-t3 mt-1">{f.note}</div>}
                </div>
              );
            })}
          </div>
          </>}

          {canFill && (
            <div className="flex gap-[6px]">
              <Bt v="secondary" icon={Save} full disabled={busy} onClick={() => save(false)}>Save</Bt>
              {!isCompleted && <Bt icon={Send} full disabled={busy} onClick={() => save(true)}>Submit</Bt>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
